import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboard, organization } from '@cwos/api-client';
import type { organization as OrgNS } from '@cwos/api-client';
import { Pill, SkeletonSet } from '@cwos/ui-admin';
import type { Severity } from '@cwos/ui-shared';
import { useNav } from '../navigation.js';
import { SettingsActions, SettingsCard, SettingsField as Field, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';
import { integrationSyncPill } from './organizationLogic.js';

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
type DayKey = (typeof DAY_KEYS)[number];
const DAY_LABELS: Record<DayKey, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type DayState = { open: string; close: string };
type HoursState = Record<DayKey, DayState>;

function emptyHours(): HoursState {
  return {
    mon: { open: '', close: '' },
    tue: { open: '', close: '' },
    wed: { open: '', close: '' },
    thu: { open: '', close: '' },
    fri: { open: '', close: '' },
    sat: { open: '', close: '' },
    sun: { open: '', close: '' },
  };
}

function fromOrg(hours: OrgNS.BusinessHours | null): HoursState {
  const state = emptyHours();
  if (!hours) return state;
  for (const d of DAY_KEYS) {
    const v = hours[d];
    if (v) state[d] = { open: v.open ?? '', close: v.close ?? '' };
  }
  return state;
}

function toOrg(state: HoursState): OrgNS.BusinessHours | null {
  const out: OrgNS.BusinessHours = {};
  let any = false;
  for (const d of DAY_KEYS) {
    const v = state[d];
    if (v.open || v.close) {
      out[d] = { open: v.open || null, close: v.close || null };
      any = true;
    } else {
      out[d] = null;
    }
  }
  return any ? out : null;
}

/**
 * Organization settings (doc 01 A6 Organization, doc 12 §12.3.1, doc 13 T13).
 * Identity + operational config over the live `GET/PUT /organization` route,
 * plus integration health (real) and a read-only security summary. No secrets:
 * address/contact are org-facing facts; credentials never appear here.
 */
export function OrgSettings() {
  const qc = useQueryClient();
  const navigate = useNav();
  const detail = useQuery({
    queryKey: ['organization', 'detail'],
    queryFn: () => organization.detail(),
  });
  const health = useQuery({
    queryKey: ['dashboard', 'integration-health'],
    queryFn: () => dashboard.integrationHealth(),
  });

  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [locale, setLocale] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [defaultWeeklyHours, setDefaultWeeklyHours] = useState('');
  const [weekStartsOn, setWeekStartsOn] = useState('1');
  const [hours, setHours] = useState<HoursState>(emptyHours());
  const [error, setError] = useState<string | null>(null);
  // Postal address and primary contact are stored on the organization and
  // accepted by PUT /organization, but had no editor — the only way to set them
  // was to call the API directly.
  const [addr, setAddr] = useState({
    line1: '',
    line2: '',
    city: '',
    region: '',
    postalCode: '',
    country: '',
  });
  const [contact, setContact] = useState({ name: '', role: '', email: '', phone: '' });
  // Organization-wide escalation default. Individual automations override it;
  // when they leave it unset, this is what they fall back to.
  const [escMinutes, setEscMinutes] = useState('');
  const [escTarget, setEscTarget] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const org = detail.data?.organization;
    if (!org) return;
    setName(org.name ?? '');
    setLegalName(org.legalName ?? '');
    setTimezone(org.timezone ?? '');
    setLocale(org.locale ?? '');
    setPhone(org.phone ?? '');
    setWebsite(org.website ?? '');
    setDefaultWeeklyHours(org.defaultWeeklyHours != null ? String(org.defaultWeeklyHours) : '');
    setWeekStartsOn(String(org.weekStartsOn ?? 1));
    setHours(fromOrg(org.businessHours));
    setAddr({
      line1: org.address?.line1 ?? '',
      line2: org.address?.line2 ?? '',
      city: org.address?.city ?? '',
      region: org.address?.region ?? '',
      postalCode: org.address?.postalCode ?? '',
      country: org.address?.country ?? '',
    });
    setContact({
      name: org.primaryContact?.name ?? '',
      role: org.primaryContact?.role ?? '',
      email: org.primaryContact?.email ?? '',
      phone: org.primaryContact?.phone ?? '',
    });
    setEscMinutes(org.defaultEscalationMinutes != null ? String(org.defaultEscalationMinutes) : '');
    setEscTarget(org.defaultEscalationTarget ?? '');
    // NOTE: `saved` is deliberately NOT cleared here. A successful save
    // invalidates this query, so clearing it on every hydrate wiped the
    // confirmation the moment the refetch landed and the user never saw it.
    // It clears when the form becomes dirty again (the banner is gated on
    // `saved && !dirty`) or on an explicit cancel.
  }, [detail.data]);

  const nonEmpty = <T extends Record<string, string>>(obj: T) =>
    Object.values(obj).some((v) => v.trim() !== '');
  const trimmed = <T extends Record<string, string>>(obj: T) =>
    Object.fromEntries(
      Object.entries(obj)
        .map(([k, v]) => [k, v.trim()])
        .filter(([, v]) => v),
    );

  const save = useMutation({
    mutationFn: () => {
      const patch: OrgNS.OrganizationPatch = {
        name: name.trim() || undefined,
        legalName: legalName.trim() || null,
        timezone: timezone.trim() || undefined,
        locale: locale.trim() || null,
        phone: phone.trim() || null,
        website: website.trim() || null,
        defaultWeeklyHours: defaultWeeklyHours.trim() ? Number(defaultWeeklyHours) : null,
        weekStartsOn: Number(weekStartsOn),
        businessHours: toOrg(hours),
        // An address or contact with every field cleared is sent as null, so
        // clearing it actually removes it rather than storing an empty shell.
        address: nonEmpty(addr) ? (trimmed(addr) as OrgNS.Address) : null,
        primaryContact: nonEmpty(contact) ? (trimmed(contact) as OrgNS.PrimaryContact) : null,
        defaultEscalationMinutes: escMinutes.trim() ? Number(escMinutes) : null,
        defaultEscalationTarget: (escTarget || null) as OrgNS.EscalationTarget | null,
      };
      return organization.update(patch);
    },
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['organization', 'detail'] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Save failed.'),
  });

  const org = detail.data?.organization;

  // Validation runs on the draft, not on submit, so a person is told about a
  // problem while they can still see the field that caused it.
  const fieldErrors: Record<string, string> = {};
  if (!name.trim()) fieldErrors.name = 'An organization needs a name.';
  if (!timezone.trim()) {
    fieldErrors.timezone = 'A timezone is required — schedules and reports depend on it.';
  } else if (!/^[A-Za-z]+\/[A-Za-z_+-]+/.test(timezone.trim()) && timezone.trim() !== 'UTC') {
    fieldErrors.timezone = 'Use an IANA name such as America/Chicago.';
  }
  if (defaultWeeklyHours.trim()) {
    const n = Number(defaultWeeklyHours);
    if (!Number.isFinite(n) || n < 0 || n > 168) fieldErrors.hours = 'Between 0 and 168 hours.';
  }
  if (escMinutes.trim()) {
    const n = Number(escMinutes);
    if (!Number.isInteger(n) || n < 0) fieldErrors.esc = 'Whole minutes, 0 or more.';
  }
  if (contact.email.trim() && !contact.email.includes('@')) {
    fieldErrors.contactEmail = 'That does not look like an email address.';
  }
  const valid = Object.keys(fieldErrors).length === 0;

  // Dirty is a comparison against what the server last returned, so undoing an
  // edit by hand leaves the form clean.
  const serverSnapshot = org
    ? JSON.stringify({
        name: org.name ?? '',
        legalName: org.legalName ?? '',
        timezone: org.timezone ?? '',
        locale: org.locale ?? '',
        phone: org.phone ?? '',
        website: org.website ?? '',
        defaultWeeklyHours: org.defaultWeeklyHours != null ? String(org.defaultWeeklyHours) : '',
        weekStartsOn: String(org.weekStartsOn ?? 1),
        hours: fromOrg(org.businessHours),
        addr: {
          line1: org.address?.line1 ?? '',
          line2: org.address?.line2 ?? '',
          city: org.address?.city ?? '',
          region: org.address?.region ?? '',
          postalCode: org.address?.postalCode ?? '',
          country: org.address?.country ?? '',
        },
        contact: {
          name: org.primaryContact?.name ?? '',
          role: org.primaryContact?.role ?? '',
          email: org.primaryContact?.email ?? '',
          phone: org.primaryContact?.phone ?? '',
        },
        escMinutes:
          org.defaultEscalationMinutes != null ? String(org.defaultEscalationMinutes) : '',
        escTarget: org.defaultEscalationTarget ?? '',
      })
    : null;
  const draftSnapshot = JSON.stringify({
    name,
    legalName,
    timezone,
    locale,
    phone,
    website,
    defaultWeeklyHours,
    weekStartsOn,
    hours,
    addr,
    contact,
    escMinutes,
    escTarget,
  });
  const dirty = serverSnapshot != null && serverSnapshot !== draftSnapshot;

  // Unsaved-change protection: only armed while there are genuinely unsaved
  // edits, so it never nags someone who has changed nothing.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const revert = () => {
    if (!org) return;
    setName(org.name ?? '');
    setLegalName(org.legalName ?? '');
    setTimezone(org.timezone ?? '');
    setLocale(org.locale ?? '');
    setPhone(org.phone ?? '');
    setWebsite(org.website ?? '');
    setDefaultWeeklyHours(org.defaultWeeklyHours != null ? String(org.defaultWeeklyHours) : '');
    setWeekStartsOn(String(org.weekStartsOn ?? 1));
    setHours(fromOrg(org.businessHours));
    setAddr({
      line1: org.address?.line1 ?? '',
      line2: org.address?.line2 ?? '',
      city: org.address?.city ?? '',
      region: org.address?.region ?? '',
      postalCode: org.address?.postalCode ?? '',
      country: org.address?.country ?? '',
    });
    setContact({
      name: org.primaryContact?.name ?? '',
      role: org.primaryContact?.role ?? '',
      email: org.primaryContact?.email ?? '',
      phone: org.primaryContact?.phone ?? '',
    });
    setEscMinutes(org.defaultEscalationMinutes != null ? String(org.defaultEscalationMinutes) : '');
    setEscTarget(org.defaultEscalationTarget ?? '');
    setError(null);
    setSaved(false);
  };

  return (
    <div className="rn-col" style={{ maxWidth: 720 }}>
      <SettingsHeader title="Organization" />

      {detail.isLoading ? (
        <SkeletonSet surface="table" rows={6} />
      ) : detail.isError && !org ? (
        <LoadFailure
          error={detail.error}
          message="We couldn’t load your organization just now."
          onRetry={() => void detail.refetch()}
        />
      ) : org ? (
        <>
          <SettingsCard>
            <h2 className="rn-card-title">Identity</h2>
            <dl
              style={{
                display: 'grid',
                gridTemplateColumns: 'max-content 1fr',
                gap: 'var(--space-8) var(--space-16)',
                margin: 'var(--space-12) 0 0',
              }}
            >
              <dt className="rn-muted">Organization ID</dt>
              <dd style={{ margin: 0, fontVariantNumeric: 'tabular-nums' }}>{org.id}</dd>
              <dt className="rn-muted">Status</dt>
              <dd style={{ margin: 0 }}>{org.status}</dd>
              <dt className="rn-muted">Slug</dt>
              <dd style={{ margin: 0 }}>{org.slug}</dd>
              <dt className="rn-muted">Jurisdiction</dt>
              <dd style={{ margin: 0 }}>{org.jurisdiction ?? '—'}</dd>
            </dl>

            <div style={{ display: 'grid', gap: 'var(--space-10)', marginTop: 'var(--space-16)' }}>
              <Field label="Display name" error={fieldErrors.name}>
                <input
                  style={{ marginBottom: 0 }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Field>
              <Field label="Legal name">
                <input
                  style={{ marginBottom: 0 }}
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                />
              </Field>
              <Field label="Phone">
                <input
                  style={{ marginBottom: 0 }}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Field>
              <Field label="Website">
                <input
                  style={{ marginBottom: 0 }}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </Field>
              <Field label="Timezone (IANA)" error={fieldErrors.timezone}>
                <input
                  style={{ marginBottom: 0 }}
                  value={timezone}
                  placeholder="America/Chicago"
                  onChange={(e) => setTimezone(e.target.value)}
                />
              </Field>
              <Field label="Locale">
                <input
                  style={{ marginBottom: 0 }}
                  value={locale}
                  placeholder="en-US"
                  onChange={(e) => setLocale(e.target.value)}
                />
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">Operational</h2>
            <div style={{ display: 'grid', gap: 'var(--space-12)', marginTop: 'var(--space-16)' }}>
              <div style={{ display: 'grid', gap: 'var(--space-8)' }}>
                <span className="rn-muted">Working hours</span>
                {DAY_KEYS.map((d) => (
                  <div
                    key={d}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '120px 1fr 1fr',
                      gap: 'var(--space-8)',
                      alignItems: 'center',
                    }}
                  >
                    <span>{DAY_LABELS[d]}</span>
                    <input
                      type="time"
                      style={{ marginBottom: 0 }}
                      value={hours[d].open}
                      onChange={(e) =>
                        setHours((h) => ({ ...h, [d]: { ...h[d], open: e.target.value } }))
                      }
                    />
                    <input
                      type="time"
                      style={{ marginBottom: 0 }}
                      value={hours[d].close}
                      onChange={(e) =>
                        setHours((h) => ({ ...h, [d]: { ...h[d], close: e.target.value } }))
                      }
                    />
                  </div>
                ))}
                <span className="rn-muted">
                  Leave both blank for a closed day. Working hours drive the Team “Working now”
                  default.
                </span>
              </div>

              <Field label="Default weekly hours" error={fieldErrors.hours}>
                <input
                  type="number"
                  min={0}
                  max={168}
                  style={{ marginBottom: 0 }}
                  value={defaultWeeklyHours}
                  onChange={(e) => setDefaultWeeklyHours(e.target.value)}
                />
              </Field>

              <Field label="Week starts on">
                <select
                  style={{ marginBottom: 0 }}
                  value={weekStartsOn}
                  onChange={(e) => setWeekStartsOn(e.target.value)}
                >
                  {WEEKDAYS.map((d, i) => (
                    <option key={d} value={String(i)}>
                      {d}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Timesheet period">
                <span style={{ margin: 0 }}>{org.timesheetPeriod}</span>
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">Address & primary contact</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              Where your organization is based, and who Phibian should reach first. Both are
              optional; clearing every field removes them.
            </p>
            <div className="set-fields" style={{ marginTop: 'var(--space-14)' }}>
              <Field label="Address line 1">
                <input
                  style={{ marginBottom: 0 }}
                  value={addr.line1}
                  onChange={(e) => setAddr((a) => ({ ...a, line1: e.target.value }))}
                />
              </Field>
              <Field label="Address line 2">
                <input
                  style={{ marginBottom: 0 }}
                  value={addr.line2}
                  onChange={(e) => setAddr((a) => ({ ...a, line2: e.target.value }))}
                />
              </Field>
              <Field label="City">
                <input
                  style={{ marginBottom: 0 }}
                  value={addr.city}
                  onChange={(e) => setAddr((a) => ({ ...a, city: e.target.value }))}
                />
              </Field>
              <Field label="State / region">
                <input
                  style={{ marginBottom: 0 }}
                  value={addr.region}
                  onChange={(e) => setAddr((a) => ({ ...a, region: e.target.value }))}
                />
              </Field>
              <Field label="Postal code">
                <input
                  style={{ marginBottom: 0 }}
                  value={addr.postalCode}
                  onChange={(e) => setAddr((a) => ({ ...a, postalCode: e.target.value }))}
                />
              </Field>
              <Field label="Country">
                <input
                  style={{ marginBottom: 0 }}
                  value={addr.country}
                  onChange={(e) => setAddr((a) => ({ ...a, country: e.target.value }))}
                />
              </Field>
            </div>

            <div className="set-fields" style={{ marginTop: 'var(--space-16)' }}>
              <Field label="Contact name">
                <input
                  style={{ marginBottom: 0 }}
                  value={contact.name}
                  onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                />
              </Field>
              <Field label="Contact role">
                <input
                  style={{ marginBottom: 0 }}
                  value={contact.role}
                  onChange={(e) => setContact((c) => ({ ...c, role: e.target.value }))}
                />
              </Field>
              <Field label="Contact email" error={fieldErrors.contactEmail}>
                <input
                  type="email"
                  style={{ marginBottom: 0 }}
                  value={contact.email}
                  onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                />
              </Field>
              <Field label="Contact phone">
                <input
                  style={{ marginBottom: 0 }}
                  value={contact.phone}
                  onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                />
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">Escalation default</h2>
            <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
              How long an unresolved attention item may sit before it escalates, and who it goes to.
              Individual automations can override this; when they don’t, they fall back to it. Leave
              blank for no organization-wide default.
            </p>
            <div className="set-fields" style={{ marginTop: 'var(--space-14)' }}>
              <Field
                label="Escalate after (minutes)"
                error={fieldErrors.esc}
                hint="e.g. 120 for two hours"
              >
                <input
                  type="number"
                  min={0}
                  style={{ marginBottom: 0 }}
                  value={escMinutes}
                  onChange={(e) => setEscMinutes(e.target.value)}
                />
              </Field>
              <Field label="Escalate to">
                <select
                  style={{ marginBottom: 0 }}
                  value={escTarget}
                  onChange={(e) => setEscTarget(e.target.value)}
                >
                  <option value="">No default</option>
                  <option value="supervisor">The worker’s assigned supervisor</option>
                  <option value="program_supervisors">All supervisors on the program</option>
                </select>
              </Field>
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">Sites</h2>
            {/* Sites are managed on their own screen now — this card is a
                pointer to it, not a second place to read them. Duplicating the
                list here is how two surfaces start disagreeing. */}
            {detail.data && detail.data.sites.length === 0 ? (
              <p className="rn-muted" style={{ marginBottom: 0 }}>
                No sites yet. Add one under Sites &amp; locations if your organization works from
                physical places — field and community teams don’t need one.
              </p>
            ) : (
              <p className="rn-muted" style={{ marginBottom: 0 }}>
                {detail.data?.sites.length} {detail.data?.sites.length === 1 ? 'site' : 'sites'}{' '}
                configured. Addresses, map positions, geofences and the programs that work there are
                all managed under Sites &amp; locations.
              </p>
            )}
            <div style={{ marginTop: 'var(--space-12)' }}>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => navigate('/settings/sites')}
              >
                Manage sites &amp; locations
              </button>
            </div>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">Capabilities & integrations</h2>
            {health.isLoading ? (
              <SkeletonSet surface="table" rows={2} />
            ) : health.isError && !health.data ? (
              <LoadFailure
                error={health.error}
                message="We couldn’t load integration health just now."
                onRetry={() => void health.refetch()}
              />
            ) : (
              <ul
                style={{
                  listStyle: 'none',
                  margin: 'var(--space-10) 0 0',
                  padding: 0,
                  display: 'grid',
                  gap: 'var(--space-8)',
                }}
              >
                {(health.data?.syncs ?? []).map((s) => (
                  <li
                    key={s.source}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      border: '1px solid var(--color-line)',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--space-8) var(--space-12)',
                    }}
                  >
                    <span>{s.source}</span>
                    <Pill variant={integrationSyncPill(s).variant as Severity} dot>
                      {integrationSyncPill(s).label}
                    </Pill>
                  </li>
                ))}
              </ul>
            )}
            <p className="rn-muted" style={{ margin: 'var(--space-10) 0 0' }}>
              Every organization in this build has the core workforce features. Add-on modules and
              entitlements are managed with your account contact.
            </p>
          </SettingsCard>

          <SettingsCard>
            <h2 className="rn-card-title">Security</h2>
            <ul
              style={{
                margin: 'var(--space-10) 0 0',
                paddingInlineStart: 'var(--space-16)',
                display: 'grid',
                gap: 'var(--space-8)',
              }}
            >
              <li className="rn-muted" style={{ color: 'var(--color-ink)' }}>
                Organization admins must sign in with multi-factor authentication, re-checked on
                every privileged session.
              </li>
              <li className="rn-muted" style={{ color: 'var(--color-ink)' }}>
                Sessions are server-side and revocable — suspending or deactivating an account ends
                its sessions immediately.
              </li>
              <li className="rn-muted" style={{ color: 'var(--color-ink)' }}>
                Secrets (like SMS credentials) are write-only and never shown after saving.
              </li>
            </ul>
          </SettingsCard>

          <SettingsActions
            dirty={dirty}
            valid={valid}
            saving={save.isPending}
            saved={saved && !dirty}
            error={error}
            onSave={() => save.mutate()}
            onCancel={revert}
            saveLabel="Save organization"
          />
        </>
      ) : null}
    </div>
  );
}
